import * as focusTrap from 'focus-trap';
import { attachSheetSwipe } from './sheetGesture.js';

// Shared by every <elm-modal> so focus-trap keeps only the topmost trap active
// when two modals are open, instead of two traps fighting over focus.
const trapStack = [];

const openModals = new Set();

const OPEN_ESCAPE_LAYER = '[data-escape-layer="open"]';

const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || event.defaultPrevented) return;

  const topmost = [...openModals].at(-1);
  if (!topmost) return;

  event.preventDefault();
  const layer = [...topmost.querySelectorAll(OPEN_ESCAPE_LAYER)].at(-1);
  if (layer) {
    layer.dispatchEvent(new CustomEvent('close'));
  } else if (topmost.dataset.dismissible === 'true') {
    topmost.dispatchEvent(new CustomEvent('close'));
  }
});

function isCKEditorPopup(node) {
  return node.closest('.ck') != null;
}

// A click on another <elm-modal> is "outside" this trap, so without allowing it
// each active trap would preventDefault the other modal's buttons — deadlocking
// the portal when two modals are open at once.
function isInAnyModal(node) {
  return node.closest('elm-modal') != null;
}

class ElmModal extends HTMLElement {
  connectedCallback() {
    this.previousActiveElement = document.activeElement;
    openModals.add(this);

    this.trap = focusTrap.createFocusTrap(this, {
      allowOutsideClick: (e) => isCKEditorPopup(e.target) || isInAnyModal(e.target),
      escapeDeactivates: false,
      returnFocusOnDeactivate: false,
      trapStack,
    });

    this.trap.activate();

    this.handleGlobalFocus = this.handleGlobalFocus.bind(this);
    document.body.addEventListener('focusin', this.handleGlobalFocus, true);

    this.setupResponsiveSwipe();
  }

  disconnectedCallback() {
    this.trap.deactivate();
    this.trap = undefined;
    openModals.delete(this);

    this.teardownResponsiveSwipe();

    if (this.previousActiveElement) {
      let focusTarget = this.previousActiveElement;

      // If focus was on a dropdown item, focus the trigger instead
      if (this.previousActiveElement.closest('.dropdown__item')) {
        const dropdown = this.previousActiveElement.closest('.dropdown');
        const trigger = dropdown?.querySelector('.dropdown__trigger');
        if (trigger) {
          focusTarget = trigger;
        }
      }

      focusTarget.focus();
    }

    this.previousActiveElement = undefined;
    document.body.removeEventListener('focusin', this.handleGlobalFocus, true);
  }

  handleGlobalFocus(e) {
    if (isCKEditorPopup(e.target)) {
      if (this.trap?.active) {
        this.trap.deactivate();
      }
    } else if (this.trap && !this.trap.active) {
      this.trap.activate();
    }
  }

  setupResponsiveSwipe() {
    if (this.dataset.dismissible !== 'true') return;
    if (this.dataset.centered === 'true') return;

    this.mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.applySwipe = () => {
      if (this.mediaQuery.matches) {
        this.attachSwipe();
      } else {
        this.detachSwipe();
      }
    };
    this.mediaQuery.addEventListener('change', this.applySwipe);
    this.applySwipe();
  }

  attachSwipe() {
    if (this.detach) return;

    const panel = this.querySelector('.modal__dialog');
    const backdrop = this.querySelector('.modal__backdrop');
    const zones = this.querySelectorAll('.modal__handle, .modal__header');
    if (!panel || !backdrop || zones.length === 0) return;

    this.detach = attachSheetSwipe({
      panel,
      backdrop,
      zones: Array.from(zones),
    });
  }

  detachSwipe() {
    if (this.detach) {
      this.detach();
      this.detach = undefined;
    }
  }

  teardownResponsiveSwipe() {
    if (this.mediaQuery && this.applySwipe) {
      this.mediaQuery.removeEventListener('change', this.applySwipe);
    }
    this.mediaQuery = undefined;
    this.applySwipe = undefined;
    this.detachSwipe();
  }
}

if (!customElements.get('elm-modal')) {
  customElements.define('elm-modal', ElmModal);
}
